/**
 * Перенос данных из кеша запросов в черновики разделов.
 *
 * Загрузка ЗАТИРАЕТ черновик, поэтому выполняется только когда правок нет.
 * Иначе фоновое обновление после чужого сохранения стирало бы правки текущего
 * пользователя — самый обидный класс потерь: пользователь ничего не делал,
 * а работа исчезла.
 *
 * Если правки есть, данные остаются как были; они подхватятся после сохранения
 * или отката, когда раздел снова станет чистым.
 */

import { useEffect, useRef } from 'react';
import type { ModuleName } from '@otc/shared';
import { useDrafts, type Entity } from './drafts.js';

/**
 * Отпечаток набора записей: идентификаторы и версии.
 *
 * Сравнение идёт по СОДЕРЖИМОМУ, а не по ссылке на массив. Это не оптимизация,
 * а защита от бесконечного цикла: экран может передать сюда производный массив
 * (например, `clinics.flatMap(c => c.legals)`), который пересоздаётся на каждом
 * рендере. По ссылке эффект срабатывал бы всегда, `load()` менял бы состояние,
 * состояние вызывало бы рендер — и так до аварийной остановки React.
 *
 * Версия входит в отпечаток потому, что она увеличивается при каждом изменении
 * записи: одинаковый отпечаток надёжно означает «данные те же».
 */
function signatureOf(entities: Entity[] | undefined): string {
  if (!entities) return '';
  let signature = '';
  for (const entity of entities) signature += `${entity.id}:${entity.version};`;
  return signature;
}

export function useSync(module: ModuleName, kind: string, entities: Entity[] | undefined): void {
  const lastSignature = useRef<string | null>(null);
  const signature = signatureOf(entities);

  useEffect(() => {
    if (!entities) return;
    if (lastSignature.current === signature) return;

    const store = useDrafts.getState();
    // Пока в разделе есть несохранённые правки, данные не трогаем: отпечаток
    // тоже не запоминаем, чтобы загрузка произошла после сохранения или отката.
    if (store.isDirty(module)) return;

    store.load(module, kind, entities);
    lastSignature.current = signature;
    // `entities` намеренно не в зависимостях: сравнение идёт по отпечатку.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, kind, signature]);
}

/**
 * Раздел из нескольких видов записей («Клиники» ведёт клиники, юрлица и
 * филиалы) синхронизируется несколькими вызовами `useSync` подряд: загрузка
 * не помечает раздел изменённым, поэтому следующие вызовы тоже сработают.
 */
